import { Button, type ButtonProps, Text } from "@mantine/core";
import { withClickAnalytics } from "@soluto-private/eventualize-react";
import { useMutation } from "@tanstack/react-query";

import type { ChatTask } from "@/sdk/agent/chat";

import { useAgentSdk } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { getSessionByTaskId } from "@/sdk/sessions";

const AnalyticButton = withClickAnalytics(Button<"button">, "Click");

export default function CloseChatButton({ chatTask, size = "sm" }: { chatTask: ChatTask } & ButtonProps) {
    const logger = useLogger({ module: "CloseChatButton", supportTeam: "messaging" });
    const agentSdk = useAgentSdk();

    const { isPending, mutateAsync: endChatTaskWrapup } = useMutation({
        mutationFn: async () => {
            if (chatTask.status === "wrapping") {
                const session = getSessionByTaskId(chatTask.id);
                if (!session) {
                    logger.error({ taskId: chatTask.id }, "Cannot close chat - no session found for task");
                    return;
                }
                await agentSdk.endWrapup("AgentTerminatedWrapping", chatTask.partner, session.id, chatTask);
            } else {
                logger.error("Task is in an invalid state to close chat");
            }
        },
    });

    return (
        <AnalyticButton
            analytics={{ actionId: "CloseChatButton" }}
            id={`close-chat-button-chatId-${chatTask.chatId}`}
            aria-label="Close chat"
            data-testid="close-chat-button"
            disabled={isPending || chatTask.status !== "wrapping"}
            loading={isPending}
            size={size}
            color="asurion.5"
            onClick={() => void endChatTaskWrapup()}
        >
            <Text size="xs">Close chat</Text>
        </AnalyticButton>
    );
}
